import { useId } from "react";
import { Crown, Gift, MessageSquare, Plug, Zap } from "lucide-react";

import { Button } from "components/common/buttons/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/TooltipV3";
import {
  type ProjectCommentsBadge,
  useProjectCommentsChrome,
} from "features/project-comments/project-comments-chrome";
import { useOpenProjectScriptAiHub } from "features/script-editor/script-ai-hub";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace/useWorkspaceIdentifiers";
import { UNLICENSED_PROJECT_LOCKED_MESSAGE } from "hooks/project/projectUpgradeUtils";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import {
  CONNECT_AI_APPS_LABEL,
  ConnectAiAppsIconButton,
  useConnectAiAppsAction,
} from "./ConnectAiAppsButton";
import { HeaderMoreMenu, type HeaderMoreMenuItem } from "./HeaderMoreMenu";
import { REFERRAL_LABEL, ReferralIconButton, useReferralAction } from "./ReferralButton";
import { formatCreditsLabel } from "./runAiCredits";

/** The Comments button's counts as words, for its accessible name and tooltip. */
function describeCommentsBadge({
  unreadThreadCount,
  unresolvedOpenThreadCount,
}: ProjectCommentsBadge): string[] {
  const parts: string[] = [];
  if (unreadThreadCount === "unavailable") {
    parts.push("unread status unavailable");
  } else if (typeof unreadThreadCount === "number" && unreadThreadCount > 0) {
    parts.push(`${unreadThreadCount} unread thread${unreadThreadCount === 1 ? "" : "s"}`);
  }
  if (unresolvedOpenThreadCount > 0) {
    parts.push(
      `${unresolvedOpenThreadCount} unresolved thread${unresolvedOpenThreadCount === 1 ? "" : "s"}`
    );
  }
  return parts;
}

const hasUnreadThreads = ({ unreadThreadCount }: ProjectCommentsBadge): boolean =>
  typeof unreadThreadCount === "number" && unreadThreadCount > 0;

export const ProjectHeaderActions = () => {
  const { workspaceId } = useWorkspaceIdentifiers();
  const projectCommentsChrome = useProjectCommentsChrome();
  const openProjectScriptAiHub = useOpenProjectScriptAiHub();
  // Same project data and rules the sidebar's AI credits link used.
  const { project } = useProjectIdentifiers();
  const creditsLabel = project ? formatCreditsLabel(project) : undefined;
  const creditsDescriptionId = useId();
  const referral = useReferralAction();
  const connect = useConnectAiAppsAction(workspaceId ?? "");
  const moreItems: HeaderMoreMenuItem[] = [
    ...(referral.available ? [{ label: REFERRAL_LABEL, icon: Gift, onSelect: referral.open }] : []),
    ...(connect.available
      ? [{ label: CONNECT_AI_APPS_LABEL, icon: Plug, onSelect: connect.open }]
      : []),
  ];

  const commentsBadgeParts =
    projectCommentsChrome != null ? describeCommentsBadge(projectCommentsChrome.commentsBadge) : [];
  const commentsTooltip =
    commentsBadgeParts.length > 0 ? `Comments · ${commentsBadgeParts.join(" · ")}` : "Comments";

  const showStandaloneUpgrade = projectCommentsChrome?.licenseLockedAction != null;

  return (
    // Groups sit apart; the icon buttons inside a group sit close together.
    <div className="flex items-center gap-2 md:gap-3">
      <div className="flex items-center gap-0.5">
        {projectCommentsChrome ? (
          <Tooltip delayDuration={400}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={["Open comments", ...commentsBadgeParts].join(", ")}
                onClick={() => projectCommentsChrome.openToolsSheet("comments")}
                className="relative inline-flex h-8 w-8 min-w-0 shrink-0 items-center justify-center p-0 rounded-md text-foreground hover:bg-muted"
              >
                <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-foreground" aria-hidden />
                  {hasUnreadThreads(projectCommentsChrome.commentsBadge) ? (
                    <span
                      className="pointer-events-none absolute right-0 top-0 h-1.5 w-1.5 shrink-0 translate-x-[4px] -translate-y-[4px] rounded-full bg-notification"
                      aria-hidden
                    />
                  ) : null}
                  {projectCommentsChrome.commentsBadge.unresolvedOpenThreadCount > 0 ? (
                    <span
                      className="pointer-events-none absolute bottom-0 right-0 h-1.5 w-1.5 shrink-0 translate-x-[4px] translate-y-[4px] rounded-full bg-primary"
                      aria-hidden
                    />
                  ) : null}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="end" sideOffset={6}>
              {commentsTooltip}
            </TooltipContent>
          </Tooltip>
        ) : null}
        {/* Priority+: icon buttons from sm up, the same actions in a More menu below sm. */}
        <div className="hidden items-center gap-0.5 sm:flex">
          {referral.available ? <ReferralIconButton onOpen={referral.open} /> : null}
          {connect.available ? <ConnectAiAppsIconButton onOpen={connect.open} /> : null}
        </div>
        <div className="flex items-center sm:hidden">
          <HeaderMoreMenu items={moreItems} />
        </div>
      </div>
      {referral.dialog}
      {connect.dialog}
      {openProjectScriptAiHub ? (
        <Tooltip delayDuration={400}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              color="gray"
              size="sm"
              // No argument: the dialog opens exactly as it always has.
              onClick={() => openProjectScriptAiHub()}
              aria-label="Run AI"
              aria-describedby={creditsLabel ? creditsDescriptionId : undefined}
              className="h-8 shrink-0 gap-2 rounded-md px-2.5 [&_svg]:h-3.5 [&_svg]:w-3.5"
            >
              <Zap className="fill-none" aria-hidden />
              <span>Run AI</span>
              {creditsLabel ? (
                <span
                  aria-hidden
                  className="hidden border-l border-border pl-2 text-xs font-normal tabular-nums text-muted-foreground sm:inline"
                >
                  {creditsLabel}
                </span>
              ) : null}
              {creditsLabel ? (
                <span id={creditsDescriptionId} className="sr-only">
                  {`${creditsLabel} left`}
                </span>
              ) : null}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" sideOffset={6} className="max-w-xs">
            Run AI for this project version: script breakdown, characters, coverage, schedule, and
            budget.
            {creditsLabel ? ` ${creditsLabel} left.` : null}
          </TooltipContent>
        </Tooltip>
      ) : null}
      {showStandaloneUpgrade && projectCommentsChrome?.licenseLockedAction ? (
        <Tooltip delayDuration={400}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghostBrand"
              color="brand"
              size="sm"
              aria-label="Upgrade"
              onClick={projectCommentsChrome.licenseLockedAction.onOpenLicensePurchase}
              className="rounded-md flex items-center gap-2"
            >
              <Crown className="w-4 h-4" aria-hidden />
              <span className="hidden text-sm sm:inline">Upgrade</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" sideOffset={6} className="max-w-xs">
            {UNLICENSED_PROJECT_LOCKED_MESSAGE}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
};
